import {
  Button,
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  Separator,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@flatfile/baseline'
import { Fragment, useMemo, useState } from 'react'
import { useIdentity } from '../../hooks/useIdentity'
import { getDashboardAnalyticsList } from '../../pages/CustomApps/hooks/getDashboardAnalyticsList'
import { ListMetric, MetricListItem } from '../../pages/CustomApps/utilities/customAppHelpers'
import useResource from '../../resources/hooks/useResource'
import { getFormattedBytes, getFormattedNumber, getFormattedTimeElapsed } from '../../utils/format'
import { spacesBaseUrl, spacesUrl } from '../../utils/pageUtils'
import { UserInfoGuest } from '../Shared/UserInfoGuest'

const renderTableHeader = (headers: { value: string; width: string }[]) => (
  <>
    {headers.map((header, index) => (
      <th key={index} style={{ width: header.width }} className='p-2 text-left align-middle'>
        <Text variant='callout' emphasized className='text-muted'>
          {header.value}
        </Text>
      </th>
    ))}
  </>
)

const renderTableRow = (cells: (string | JSX.Element | undefined)[]) => (
  <>
    {cells.map((cell, index) => (
      <td key={index} className='p-2 text-left align-middle border-b border-gray-200'>
        {cell}
      </td>
    ))}
  </>
)

const UserList = ({ users, spaceId = '' }: { users?: string; spaceId?: string }) => {
  const space = useResource('SPACES', spaceId)
  const userArray = useMemo(() => users?.split(', ') ?? [], [users])
  return (
    <div className='flex gap-1'>
      {userArray.map((user, index) => (
        <Fragment key={`${user}-${index}`}>
          {space?.metadata?.userInfo?.name && user === 'Anonymous Guest' ? (
            <UserInfoGuest key={`${user}-${index}`} space={space} />
          ) : (
            <Text key={`${user}-${index}`} variant='callout'>
              {user}
              {index < userArray.length - 1 ? ',' : ''}
            </Text>
          )}
        </Fragment>
      ))}
    </div>
  )
}
const renderSpaceName = (spaceId?: string, spaceName?: string, token?: string) => {
  return (
    <div className='flex'>
      {spaceId && token ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={() => window.open(spacesUrl(spaceId, token), '_blank', 'noopener,noreferrer')}
              variant='ghost'
              size='sm'
              className='px-0 text-primary'
              icon='arrow-up-right'
              iconPlacement='right'
              iconProps={{ size: 16 }}
            >
              {spaceName}
            </Button>
          </TooltipTrigger>
          <TooltipContent className='flex items-center px-2 py-1'>
            <Text variant='body' className='text-sm'>
              Open in new tab
            </Text>
          </TooltipContent>
        </Tooltip>
      ) : null}
    </div>
  )
}

const renderFileName = (
  workbookId: string = '',
  spaceId: string = '',
  name: string = '',
  spaceName: string = '',
  token: string = ''
) => {
  return (
    <div className='flex flex-col gap-0'>
      <div className='flex'>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={() => {
                window.open(`${spacesBaseUrl(token)}/resource/${workbookId}`, '_blank', 'noopener,noreferrer')
              }}
              variant='ghost'
              size='sm'
              className='px-0 text-primary font-medium'
              icon='arrow-up-right'
              iconPlacement='right'
              iconProps={{ size: 16 }}
            >
              {name}
            </Button>
          </TooltipTrigger>
          <TooltipContent className='flex items-center px-2 py-1'>
            <Text variant='body' className='text-sm'>
              Open File workbook in new tab
            </Text>
          </TooltipContent>
        </Tooltip>
      </div>
      <div className='flex'>
        <Tooltip>
          <TooltipTrigger asChild>
            <Text
              as='button'
              className='cursor-pointer px-0 text-gray-700 text-xs font-normal'
              onClick={() => window.open(spacesUrl(spaceId, token), '_blank', 'noopener,noreferrer')}
            >
              {spaceName}
            </Text>
          </TooltipTrigger>
          <TooltipContent className='flex items-center px-2 py-1'>
            <Text variant='body' className='text-sm'>
              Open Space in new tab
            </Text>
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  )
}

const FileHeaderComponent = () =>
  renderTableHeader([
    { value: 'File', width: '27%' },
    { value: 'Type', width: '7%' },
    { value: 'Size', width: '9%' },
    { value: 'Uploaded by', width: '17%' },
    { value: 'Timestamp', width: '17%' },
  ])

const FileRowComponent = ({
  name,
  extension,
  size,
  spaceName,
  spaceId,
  username,
  createdAt,
  workbookId,
}: MetricListItem) => {
  const { identity } = useIdentity()
  return renderTableRow([
    renderFileName(workbookId, spaceId, name, spaceName, identity.token),
    <div key='extension' className='inline-block bg-gray-100 px-2 py-1 rounded text-sm font-semibold'>
      {extension}
    </div>,
    <Text key='size' variant='callout' emphasized>
      {getFormattedBytes(size || 0)}
    </Text>,
    <UserList users={username} spaceId={spaceId} key='users' />,
    getFormattedTimeElapsed(new Date(createdAt || 0)),
  ])
}

// Note: Ensure renderUserList and getFormattedTimeElapsed are modified to accept a key prop if they return JSX elements

const PdvBySpaceHeaderComponent = () =>
  renderTableHeader([
    { value: 'PDV', width: '13%' },
    { value: 'Space Name', width: '27%' },
    { value: 'Users', width: '34%' },
    { value: 'Files', width: '9%' },
    { value: 'Last upload', width: '17%' },
  ])

const PdvBySpaceRowComponent = ({ pdv, name, id, users, fileCount, lastUpload }: MetricListItem) => {
  const { identity } = useIdentity()
  return renderTableRow([
    <Text key='pdv' variant='callout' emphasized>
      {getFormattedNumber(pdv || 0)}
    </Text>,
    renderSpaceName(id, name, identity.token),
    <UserList spaceId={id} users={users} key='users' />,
    <Text key='fileCount' variant='callout' emphasized>
      {fileCount?.toLocaleString()}
    </Text>,
    getFormattedTimeElapsed(new Date(lastUpload || 0)),
  ])
}

const UsersHeaderComponent = () =>
  renderTableHeader([
    { value: 'Uploaded by', width: '30%' },
    { value: 'File count', width: '15%' },
    { value: 'Data', width: '15%' },
    { value: 'PDV', width: '23%' },
    { value: 'Last upload', width: '17%' },
  ])

const UsersRowComponent = ({ username, fileCount, fileSize, pdv, lastUpload, spaceId }: MetricListItem) =>
  renderTableRow([
    <UserList users={username} spaceId={spaceId} key='users' />,
    <Text key='fileCount' variant='callout' emphasized>
      {fileCount?.toLocaleString()}
    </Text>,
    <Text key='fileSize' variant='callout' emphasized>
      {getFormattedBytes(fileSize || 0)}
    </Text>,
    <Text key='pdv' variant='callout' emphasized>
      {getFormattedNumber(pdv || 0)}
    </Text>,
    getFormattedTimeElapsed(new Date(lastUpload || 0)),
  ])

const TableComponents = {
  fileList: {
    HeaderComponent: FileHeaderComponent,
    RowComponent: FileRowComponent,
  },
  pdvBySpaceList: {
    HeaderComponent: PdvBySpaceHeaderComponent,
    RowComponent: PdvBySpaceRowComponent,
  },
  usersList: {
    HeaderComponent: UsersHeaderComponent,
    RowComponent: UsersRowComponent,
  },
}

export const MetricList = ({
  title,
  tooltip,
  currentApp,
  environment,
  startDate,
  endDate,
  metrics,
  height,
}: {
  title: string
  tooltip: string
  currentApp: any
  environment: any
  startDate: string
  endDate: string
  metrics: ListMetric
  height: number
}) => {
  const [currentPage, setCurrentPage] = useState(1)
  const { analyticData: data, pageCount } = getDashboardAnalyticsList(
    currentApp.id,
    environment.id,
    startDate,
    endDate,
    metrics,
    currentPage
  )

  const { HeaderComponent, RowComponent } = TableComponents[metrics]
  return (
    <>
      <div className='min-h-[77px]'>
        <div className='flex justify-between p-4'>
          <div className='flex items-center w-full'>
            <Text variant='title3' emphasized>
              {title}
            </Text>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size='icon-md'
                  variant='ghost'
                  className='ml-1'
                  icon='circle-info'
                  iconProps={{ size: 16 }}
                ></Button>
              </TooltipTrigger>
              <TooltipContent className='flex items-center px-2 py-1 max-w-[300px] whitespace-normal'>
                <Text variant='body' className='text-sm'>
                  {tooltip}
                </Text>
              </TooltipContent>
            </Tooltip>
          </div>

          <Pagination>
            <PaginationContent>
              {(() => {
                const maxVisiblePages = 5
                let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
                const endPage = Math.min(pageCount, startPage + maxVisiblePages - 1)

                // Adjust start page if we're near the end
                if (endPage - startPage + 1 < maxVisiblePages) {
                  startPage = Math.max(1, endPage - maxVisiblePages + 1)
                }

                // Generate array of page numbers to display
                const pages = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i)

                return pages.map((pageNum) => (
                  <PaginationItem key={pageNum}>
                    <PaginationLink
                      size='icon-md'
                      isActive={currentPage === pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </PaginationLink>
                  </PaginationItem>
                ))
              })()}
            </PaginationContent>
          </Pagination>
        </div>
        <Separator />
      </div>

      <div className='overflow-auto' style={{ height: `${height - 77}px` }}>
        <table className='w-full border-collapse'>
          <thead className='border-b border-gray-200'>
            <tr>
              <HeaderComponent />
            </tr>
          </thead>
          <tbody>
            {data.map((row, index) => (
              <tr key={row.id || index} className='hover:bg-gray-50 rounded-md'>
                <RowComponent {...row} />
              </tr>
            ))}
          </tbody>
        </table>

        {data.length === 0 && (
          <div className='flex items-center justify-center py-12'>
            <Text className='text-sm text-text-default'>No data available for this time period</Text>
          </div>
        )}
      </div>
    </>
  )
}
